import { useMemo } from 'react';
import useSWR, { mutate } from 'swr';

import axios, { fetcher, endpoints, axiosInstance, conditionalSWRKey } from 'src/utils/axios';

import { HOST_API } from 'src/config-global';

import type { IUserPage, IUserAccount } from '../types/user-account';

// ----------------------------------------------------------------------

export const ME_ENDPOINT = `${HOST_API}/api/user-accounts/me/?populate[user_page]=true&populate[active_user_page][populate][company][populate]=company_subscription&populate[working_user_pages][populate][company][populate]=company_subscription`;

const USER_ACCOUNT_URL = endpoints.user_account.root;
const options = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};

// ----------------------------------------------------------------------

export const getUserAccount = async () => {
  try {
    const res = await axiosInstance.get(ME_ENDPOINT);
    return res.data.data as IUserAccount;
  } catch (error) {
    console.error(`Could not get user account with error ${JSON.stringify(error, null, 2)} `);
    throw new Error('Could Not Get User Account');
  }
};

// ----------------------------------------------------------------------
// Helper: determine the business page documentId from active or working pages
// ----------------------------------------------------------------------
export const isProfileComplete = (userAccount?: IUserAccount | null): boolean =>
  !!userAccount?.name && !!userAccount?.last_name;

export const getBusinessDocumentId = (userAccount?: IUserAccount | null) => {
  const businessPage = getCurrentBusinessPage(userAccount);
  return businessPage?.documentId || '';
};

// Return the active business page, or the first business working page
export const getCurrentBusinessPage = (userAccount?: IUserAccount | null) => {
  if (!userAccount) return null;
  const { active_user_page: activePage, working_user_pages: workingPages = [] } = userAccount;
  if (activePage && activePage.type === 'business') {
    return activePage;
  }
  return workingPages.find((page: IUserPage) => page.type === 'business') || null;
};

// ----------------------------------------------------------------------

export function useGetUserAccount(shouldFetch: boolean = true) {
  const {
    data,
    error,
    isLoading,
    mutate: mutateFn,
    isValidating,
  } = useSWR<IUserAccount>(shouldFetch ? ME_ENDPOINT : null, () => getUserAccount(), {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    shouldRetryOnError: true,
    errorRetryCount: 3,
    errorRetryInterval: 2000,
    dedupingInterval: 60000, // Cache for 1 minute
    onError: (err) => {
      console.error('Error fetching user account:', err);
    },
  });

  return {
    userAccount: data,
    userAccountLoading: isLoading,
    userAccountError: error,
    userAccountValidating: isValidating,
    refreshUserAccount: mutateFn,
  };
}

// ----------------------------------------------------------------------

export const useGetMyUserAccount = () => {
  // Use conditionalSWRKey to prevent fetching until auth is ready
  const { data, isLoading, error, isValidating } = useSWR(
    conditionalSWRKey(`${USER_ACCOUNT_URL}/me/`),
    fetcher,
    options
  );
  const memoizedValue = useMemo(
    () => ({
      userAccount: (data?.data as IUserAccount) || null,
      userAccountLoading: isLoading,
      userAccountError: error,
      userAccountValidating: isValidating,
      userAccountEmpty: !isLoading && !data?.data,
      userAccountMeta: (!isLoading && data?.meta) || null,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
};

// ----------------------------------------------------------------------

export async function updateUserAccount(userAccountData: Partial<IUserAccount>) {
  try {
    const data = { ...userAccountData };
    await axios.put(`${USER_ACCOUNT_URL}/me/`, { data });
    mutate(
      (key) => typeof key === 'string' && key.startsWith(USER_ACCOUNT_URL),
      (currentData: any) => {
        if (Array.isArray(currentData?.data)) {
          const userAccounts: IUserAccount[] = currentData.data.map((userAccount: IUserAccount) =>
            userAccount.id === userAccountData.id
              ? { ...userAccount, ...userAccountData }
              : userAccount
          );

          return {
            ...currentData,
            data: userAccounts,
          };
        }
        return {
          data: { ...currentData, ...userAccountData },
        };
      },
      false
    );
  } catch (e) {
    console.error(`Could not update userAccount ${JSON.stringify(e, null, 2)}`);
    throw e;
  }
}

// ----------------------------------------------------------------------

export async function updateActiveUserPage(userPageDocumentId: string) {
  if (!userPageDocumentId) {
    throw new Error('userPageDocumentId is required');
  }

  try {
    const response = await axios.put(`${USER_ACCOUNT_URL}/me/active-working-page`, {
      data: {
        active_user_page: userPageDocumentId,
      },
    });

    // Invalidate the user account cache to refetch
    mutate(ME_ENDPOINT);
    mutate((key) => typeof key === 'string' && key.startsWith(USER_ACCOUNT_URL));

    return response.data;
  } catch (e: any) {
    console.error(`Could not update active user page: ${JSON.stringify(e?.message ?? e, null, 2)}`);
    throw e;
  }
}

// ----------------------------------------------------------------------

export async function deleteuserAccount(userAccountId: number) {
  try {
    await axios.delete(USER_ACCOUNT_URL + userAccountId);
    await mutate(
      USER_ACCOUNT_URL,
      (currentData: any) => {
        const userAccounts: IUserAccount[] = currentData.data.filter(
          (userAccount: IUserAccount) => userAccount.id !== userAccountId
        );
        return {
          ...currentData,
          data: userAccounts,
        };
      },
      false
    );
  } catch (e) {
    console.error(`Could not delete userAccount ${JSON.stringify(e.message, null, 2)}`);
  }
}
